import { BarChart3, Clock3, Database, Factory, Globe2, Layers, MapPinned, Scale, WalletCards, Weight } from 'lucide-react';
import PublicPageHeader from '../components/public/PublicPageHeader';
import SectionLabel from '../components/public/SectionLabel';

const items = [
  {
    icon: Layers,
    title: 'Fonti diverse, domande diverse',
    body: 'MIMIT descrive impianti e territori, MASE/SISEN la composizione mensile nazionale, il Weekly Oil Bulletin il confronto settimanale tra Paesi ed Eurostat il contesto annuale di prezzi e redditi. Le serie restano distinte.',
    tone: 'secondary',
  },
  {
    icon: Globe2,
    title: 'Il confronto europeo è settimanale',
    body: 'La pagina Europa usa i valori ufficiali con imposte, senza imposte e la media UE pubblicati dalla Commissione. Il divario fiscale è calcolato come differenza fra lordo e netto, senza mescolare la statistica mensile MASE o gli indicatori annuali Eurostat.',
    tone: 'primary',
  },
  {
    icon: Scale,
    title: 'Il PLI non è inflazione',
    body: 'Il Price Level Index HFCE di Eurostat confronta il livello generale dei prezzi dei consumi delle famiglie tra Paesi, con UE27_2020 = 100. Non misura la variazione dei prezzi nel tempo e non usa la categoria carburanti per correggere il carburante.',
    tone: 'secondary',
  },
  {
    icon: WalletCards,
    title: 'Reddito disponibile mediano equivalente',
    body: 'L’accessibilità usa il valore annuale in EUR del dataset Eurostat ilc_di03, totale per età e sesso. Un dodicesimo serve alla sola normalizzazione mensile: Eurostat non pubblica qui uno stipendio medio né il reddito di ogni famiglia.',
    tone: 'tertiary',
  },
  {
    icon: Clock3,
    title: 'Una rilevazione al giorno',
    body: 'Il file MIMIT rappresenta i prezzi in vigore alle ore 8. I controlli delle 9, 12 e 18 servono a intercettare revisioni della stessa giornata, non a creare tre prezzi diversi.',
    tone: 'primary',
  },
  {
    icon: Scale,
    title: 'Media e mediana',
    body: 'La media è semplice e non ponderata sui litri venduti. La mediana riduce l’effetto dei valori estremi. Puoi alternarle nei confronti territoriali.',
    tone: 'secondary',
  },
  {
    icon: BarChart3,
    title: 'Il singolo impianto resta separato',
    body: 'Nel distributore il grafico mostra il prezzo effettivamente presente nel dataset di ogni giornata. Le linee di comune, provincia, regione e Italia sono invece statistiche aggregate.',
    tone: 'tertiary',
  },
  {
    icon: MapPinned,
    title: 'Coordinate dichiarate',
    body: 'Le coordinate dell’anagrafica sono fornite su base volontaria dagli impianti e possono essere mancanti o imprecise. La mappa esclude valori geograficamente non plausibili.',
    tone: 'secondary',
  },
  {
    icon: Weight,
    title: 'Unità diverse',
    body: 'Benzina, gasolio e GPL sono espressi in euro per litro; il metano in euro per chilogrammo. Le serie con unità differenti non vengono mescolate.',
    tone: 'primary',
  },
  {
    icon: Database,
    title: 'Lo storico non viene sovrascritto',
    body: 'Ogni revisione importata viene conservata. Per la visualizzazione si usa l’ultima revisione valida della giornata, mantenendo intatto lo storico tecnico delle acquisizioni.',
    tone: 'tertiary',
  },
  {
    icon: Factory,
    title: 'Il netto non è il petrolio',
    body: 'Il netto MASE è il prezzo del carburante prima delle imposte. Comprende materia prima, raffinazione, logistica, distribuzione e margini. Un’eventuale serie Brent avrà fonte, unità e asse separati.',
    tone: 'primary',
  },
];

const toneClasses = {
  primary: 'bg-md-primary-container text-md-on-primary-container',
  secondary: 'bg-md-secondary-container text-md-on-secondary-container',
  tertiary: 'bg-md-tertiary-container text-md-on-tertiary-container',
};

export default function MethodologyPage() {
  return (
    <div>
      <PublicPageHeader
        title="Capire il numero prima di condividerlo."
        description="La webapp affianca MIMIT, MASE/SISEN, Weekly Oil Bulletin ed Eurostat. Qui trovi il ruolo distinto di prezzi nominali, livello generale dei prezzi e reddito disponibile mediano."
      />

      <div className="mx-auto max-w-5xl px-4 pb-14 pt-8 sm:px-6 sm:pt-12">
        <div className="grid gap-4 md:grid-cols-2">
          {items.map(({ icon: Icon, title, body, tone }, index) => (
            <article key={title} className={`relative overflow-hidden rounded-[36px_36px_14px_36px] p-6 sm:p-7 ${toneClasses[tone]} ${index === 0 ? 'md:col-span-2 md:grid md:grid-cols-[auto_1fr] md:items-center md:gap-8' : ''}`}>
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[18px] bg-md-surface text-md-on-surface shadow-sm"><Icon className="h-5 w-5" /></div>
              <div className={index === 0 ? '' : 'mt-8'}>
                <h2 className="text-2xl font-medium leading-tight tracking-[-0.025em]">{title}</h2>
                <p className="mt-3 max-w-2xl text-sm leading-6">{body}</p>
              </div>
              <div aria-hidden="true" className="absolute -bottom-9 -right-8 h-24 w-32 rounded-[52px_52px_12px_52px] bg-md-surface" />
            </article>
          ))}
        </div>

        <section className="mt-8 rounded-[40px_16px_40px_40px] bg-md-surface-container p-6 sm:p-8">
          <SectionLabel tone="neutral" className="bg-md-surface">Nota importante</SectionLabel>
          <h2 className="mt-4 max-w-3xl text-2xl font-medium leading-tight tracking-[-0.025em] text-md-on-surface sm:text-3xl">La “media nazionale” non misura quanto spende in media un automobilista.</h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-md-on-surface-variant">Il dataset non contiene i litri venduti da ciascun impianto. Per questo la media nazionale è una media dei prezzi comunicati dagli impianti osservati, non una media ponderata sui consumi effettivi.</p>
        </section>
      </div>
    </div>
  );
}
